'use client';

import { c as _c } from "react/compiler-runtime";
import type { RecommendedPlanResult, TrialUsage } from '@/lib/billing/recommended-plan';
import type { BillingInterval, PaidPlan } from '@/types/billing-plan';
import { useSearchParams } from 'next/navigation';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { BillingCycleToggle } from './components/billing-cycle-toggle';
import { CheckoutSection } from './components/checkout-section';
import { PlanChangeView } from './components/plan-change-view';
import { PlanComparisonCards, PlanComparisonCardsSkeleton } from './components/plan-comparison-cards';
import { PricingPageShell } from './components/pricing-page-shell';
import { ResumeView } from './components/resume-view';
import { usePricingActions } from './hooks/use-pricing-actions';
import { parseCheckoutQuantityParam } from './parse-checkout-quantity-param';
import { getPlanComparisonCards, RECOMMENDED_PLAN } from './plan-comparison';
import { useRecommendedPlan } from '@/data/billing/use-recommended-plan';
import { useReviewLineLimits } from '@/data/billing/use-review-line-limits';
import { useUpdateInstallationSettings } from '@/data/installations/update-installation-settings';
import { useInstallation } from '@/data/installations/use-installation';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import { getTeamOverLimitPercent, MIN_TEAM_OVER_LIMIT_PERCENT } from '@/lib/billing/recommended-plan/team-over-limit';
import { isPaidPlan, PAID_PLANS } from '@/types/billing-plan';
const BILLING_CYCLE_QUERY_PARAM = 'billing_cycle';
const BILLING_CYCLE_QUERY_VALUES = ['monthly', 'yearly'] as const satisfies readonly BillingInterval[];
function getDisplayedTrialUsage(recommendation: RecommendedPlanResult | undefined, hasExplicitPlanParam: boolean): TrialUsage | undefined {
  if (hasExplicitPlanParam) {
    return undefined;
  }
  return recommendation?.trialUsage;
}
function CheckoutView() {
  const searchParams = useSearchParams();
  const installationId = searchParams.get('installation_id');
  const quantityParam = searchParams.get('quantity');
  const planParam = searchParams.get('plan');
  const defaultPlan: PaidPlan = isPaidPlan(planParam) ? planParam : RECOMMENDED_PLAN;
  const [billingCycle, setBillingCycle] = useQueryState(BILLING_CYCLE_QUERY_PARAM, parseAsStringLiteral(BILLING_CYCLE_QUERY_VALUES).withDefault('monthly').withOptions({
    history: 'replace',
    clearOnDefault: true,
    scroll: false
  }));
  const [selectedPlan, setSelectedPlan] = useState<PaidPlan | null>(null);
  const [shouldScrollToCheckout, setShouldScrollToCheckout] = useState(false);
  const [quantity, setQuantity] = useState<number | null>(() => parseCheckoutQuantityParam(quantityParam));
  const [autoAssignSeats, setAutoAssignSeats] = useState(true);
  const checkoutSectionRef = useRef<HTMLDivElement | null>(null);
  const {
    isLoading,
    setIsLoading,
    startCheckoutFlow
  } = usePricingActions();
  const parsedInstallationId = Number(installationId) || undefined;
  const {
    data: installation,
    isPending: isInstallationLoading
  } = useInstallation(parsedInstallationId);
  const {
    data: recommendation,
    isError: isRecommendationError
  } = useRecommendedPlan(parsedInstallationId, quantity);
  const {
    data: reviewLineLimits
  } = useReviewLineLimits(parsedInstallationId);
  const {
    isPending: isUpdatingAutoAssign,
    mutate: updateInstallationSettings
  } = useUpdateInstallationSettings();
  const hasExplicitPlanParam = isPaidPlan(planParam);
  const isTrialUsageSettled = recommendation !== undefined || isRecommendationError;
  // Wait for the first recommendation response before rendering the cards. The trial-usage
  // markers on the cards come from it, so they would otherwise pop in after the cards render.
  const isRecommendationResolved = !parsedInstallationId || hasExplicitPlanParam || isTrialUsageSettled;
  const hasAppliedRecommendation = useRef(false);
  useEffect(() => {
    if (installationId) {
      trackClientEvent('pricing_page_viewed', installationId);
    }
  }, [installationId]);
  useEffect(() => {
    if (isUpdatingAutoAssign) {
      return;
    }
    setAutoAssignSeats(installation?.stripeAutomaticallyAssignAndRemoveSeats ?? true);
  }, [installation?.stripeAutomaticallyAssignAndRemoveSeats, isUpdatingAutoAssign]);
  const planComparisonCards = getPlanComparisonCards({
    billingCycle,
    selectedPlan: selectedPlan ?? defaultPlan,
    trialUsage: getDisplayedTrialUsage(recommendation, hasExplicitPlanParam),
    reviewLineLimits
  });

  // Shared recommendation context attached to funnel events so we can analyze
  // conversions against trial usage (volume vs. features used).
  const recommendationEventProps = useMemo(() => {
    const linesReviewed = recommendation?.trialUsage.linesReviewed ?? null;
    const featuresUsed = recommendation?.trialUsage.featuresUsed ?? [];
    return {
      recommended_plan: RECOMMENDED_PLAN,
      recommended_by_volume: linesReviewed != null && getTeamOverLimitPercent(linesReviewed) >= MIN_TEAM_OVER_LIMIT_PERCENT,
      recommended_by_features: featuresUsed.length >= 2,
      used_pro_features: featuresUsed,
      used_pro_features_count: featuresUsed.length,
      projected_monthly_lines: linesReviewed?.projectedMonthly ?? null,
      team_monthly_allowance: linesReviewed?.teamMonthlyAllowance ?? null,
      // A developer's auto-fix pace against Team's pool, so conversions can be read against it.
      projected_monthly_auto_fixes: recommendation?.trialUsage.autoFixes?.projectedMonthly ?? null,
      team_monthly_auto_fixes: recommendation?.trialUsage.autoFixes?.teamMonthlyAllowance ?? null,
      reviewed_lines_per_developer: linesReviewed?.perDeveloper ?? null
    };
  }, [recommendation]);

  // Runs once trial usage settles so the pro_plan_recommended event carries that context.
  useEffect(() => {
    if (hasAppliedRecommendation.current || hasExplicitPlanParam || !isTrialUsageSettled) {
      return;
    }
    hasAppliedRecommendation.current = true;
    if (selectedPlan === null) {
      setSelectedPlan(RECOMMENDED_PLAN);
    }
    trackClientEvent('pro_plan_recommended', installationId, {
      ...recommendationEventProps,
      seats: quantity
    });
  }, [hasExplicitPlanParam, isTrialUsageSettled, selectedPlan, installationId, quantity, recommendationEventProps]);
  const handleCtaClick = (plan: PaidPlan) => {
    trackClientEvent('pricing_plan_selected', installationId, {
      ...recommendationEventProps,
      plan,
      plan_tier: PAID_PLANS[plan].tier,
      quantity,
      billing_cycle: billingCycle,
      is_recommended_plan: plan === RECOMMENDED_PLAN
    });
    setSelectedPlan(plan);
    setShouldScrollToCheckout(true);
  };
  useEffect(() => {
    if (!shouldScrollToCheckout || !selectedPlan || !parsedInstallationId) {
      return;
    }
    checkoutSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
    setShouldScrollToCheckout(false);
  }, [parsedInstallationId, selectedPlan, shouldScrollToCheckout]);
  const handleAutoAssignToggle = () => {
    if (!installationId || !installation) {
      return;
    }
    const nextAutoAssignSeats = !autoAssignSeats;
    setAutoAssignSeats(nextAutoAssignSeats);
    updateInstallationSettings({
      installationId: Number(installationId),
      stripeAutomaticallyAssignAndRemoveSeats: nextAutoAssignSeats
    });
  };
  const handleCheckout = async () => {
    if (!installationId) {
      toast.error('Missing selected installation. Please go back to settings and select an installation.');
      return;
    }
    if (!quantity || quantity < 1) {
      toast.error('Quantity must be at least 1.');
      return;
    }
    if (!selectedPlan) {
      return;
    }
    trackClientEvent('checkout_started', installationId, {
      ...recommendationEventProps,
      plan: selectedPlan,
      plan_tier: PAID_PLANS[selectedPlan].tier,
      quantity,
      billing_cycle: billingCycle,
      auto_assign_seats: autoAssignSeats,
      is_recommended_plan: selectedPlan === RECOMMENDED_PLAN
    });
    setIsLoading(true);
    try {
      const parsedInstallation = Number.parseInt(installationId, 10);
      const redirectUrl = await startCheckoutFlow({
        installationId: parsedInstallation,
        quantity,
        billingCycle,
        plan: selectedPlan,
        autoAssignSeats
      });
      window.location.href = redirectUrl;
    } catch (error) {
      const errorMessage = error instanceof Error && error.message ? error.message : 'Failed to start checkout. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  return <PricingPageShell title="Choose your plan" headerExtra={<BillingCycleToggle value={billingCycle} onChange={setBillingCycle} />} containerClassName="max-w-screen-xl lg:px-8">
      {isRecommendationResolved ? <PlanComparisonCards cards={planComparisonCards} selectedPlan={selectedPlan} onCtaClick={handleCtaClick} /> : <PlanComparisonCardsSkeleton />}

      {selectedPlan && parsedInstallationId && <div ref={checkoutSectionRef}>
          <CheckoutSection plan={selectedPlan} billingCycle={billingCycle} installationId={parsedInstallationId} quantity={quantity} setQuantity={setQuantity} autoAssignSeats={autoAssignSeats} isUpdatingAutoAssign={isUpdatingAutoAssign} onAutoAssignToggle={handleAutoAssignToggle} onCheckout={handleCheckout} isLoading={isLoading} isInstallationLoading={isInstallationLoading} />
        </div>}
    </PricingPageShell>;
}
export default function Page() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Suspense fallback={null}><PricingPageContent /></Suspense>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function PricingPageContent() {
  const $ = _c(3);
  const searchParams = useSearchParams();
  const mode = searchParams.get("mode");
  if (mode === "resume") {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <ResumeView />;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  if (mode === "change") {
    let t0;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <PlanChangeView />;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  let t0;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <CheckoutView />;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}